// src/components/admin/SaleControl.tsx

import React, { useState } from 'react';
import { Tag } from 'lucide-react';
import { SaleModal } from './SaleModal';

interface SaleControlProps {
  isActive: boolean;
  saleStart: string;
  onStartSale: (data: any) => void;
  onEndSale: () => void;
  saleExpiry?: string | null;
  discount?: number | null;
  basePrice: number;
  productName: string;
  productImage?: string;
  variantId?: number | null;
  isVariant?: boolean;
  disabled?: boolean;
}

export const SaleControl: React.FC<SaleControlProps> = ({
  isActive,
  saleStart,
  onStartSale,
  onEndSale,
  saleExpiry,
  discount,
  basePrice,
  productName,
  productImage,
  variantId = null,
  isVariant = false,
  disabled = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  const isScheduled = discount && saleExpiry && (new Date(saleStart) > new Date());

  const handleStartSale = (data: any) => {
    onStartSale({ ...data, variantId });
    setShowModal(false);
  };

  // Calculate time until scheduled start
  const getTimeUntilStart = (startDate: string) => {
    const start = new Date(startDate);
    const now = new Date();
    const diff = start.getTime() - now.getTime();

    if (diff < 0) return 'Starting soon';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `Starts in ${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
    if (minutes > 0) return `Starts in ${minutes}m`;
    return 'Starting soon';
  };

  if (disabled) {
    return (
      <button
        type="button"
        disabled
        className="px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed flex items-center gap-2 text-sm"
      >
        <Tag size={16} />
        Sales not available
      </button>
    );
  }

  if (isActive && saleExpiry) {
    return (
      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
        Sale Active
      </span>
    );
  }

  return (
    <>
      {
        isScheduled && saleStart ? (
          <div className="flex items-center gap-2">
            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
              Scheduled
            </span>
            <span className="text-xs text-gray-500">
              {getTimeUntilStart(saleStart)}
            </span>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              Reschedule
            </button>
          </div>
        ) :
          <>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Tag size={16} />
              Start Sale
            </button>
          </>
      }

      {/* Sale Modal */}
      {showModal && (
        <SaleModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={handleStartSale}
          productName={productName}
          productImage={productImage}
          basePrice={basePrice}
          variantId={variantId}
          isVariant={isVariant}
        />
      )}
    </>
  );
};